import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  updateStatus,
  deletePerson,
  saveNotes,
  updateLead
} from '../../store/thunks/leadsActions';
import EmailComposer from './EmailComposer';
import EmailThread from './EmailThread';
import AddEmailForm from './AddEmailForm';
import AddPersonForm from './AddPersonForm';
import NotesEditor from './NotesEditor';
import request from '../../utils/request';

export default function LeadDetailPanel({ leadId, onDelete, onClose, onEmailSent }) {
  const [lead, setLead] = useState(null);
  const [editField, setEditField] = useState(null);
  const [selectedTo, setSelectedTo] = useState('');
  const [prevStatus, setPrevStatus] = useState(null);
  const [notesHistory, setNotesHistory] = useState([])
  const dispatch = useDispatch();

const fetchLead = () => {
  if (!leadId) return;
  request.get(`/leads/${leadId}`, { withCredentials: true })
    .then(({ data }) => {
      setLead(data.lead);
      if (data.lead.notes_history) {
        setNotesHistory(data.lead.notes_history);
      } else if (data.lead.notes) {
        setNotesHistory([{
          timestamp: data.lead.updatedAt || Date.now(),
          content: data.lead.notes
        }]);
      }
    })
    .catch(console.error);
};

  // 1) Load lead whenever leadId changes

useEffect(() => {
  if (!leadId) return setLead(null);
  fetchLead();
}, [leadId]);


  // 2) Generic save helper — **fixed** to use the returned lead directly
  const save = (field, value) => {
    dispatch(updateLead({ leadId, updates: { [field]: value } }))
      .unwrap()
      .then(updatedLead => {
        setLead(updatedLead);
        setPrevStatus(updateLead.lead.status);
        setEditField(null);
      })
      .catch(console.error);
  };

  // 3) Reply handler
  const handleReply = ({ from }) => {
    setSelectedTo(from);
  };

  if (!lead) {
    return (
      <div className="flex-1 bg-white p-6 text-gray-500">
        Select a lead
      </div>
    );
  }

  const currentThreadId = lead.messages?.length
    ? lead.messages[lead.messages.length - 1].threadId
    : null;

const handleSaveNotes = newText => {
  dispatch(saveNotes({ leadId, notes: newText }))
    .unwrap()
    .then(updatedLead => {
      setLead(updatedLead);
      if (updatedLead.notes_history) {
        setNotesHistory(updatedLead.notes_history);
      }
    })
    .catch(console.error);
};


  return (
    <div className="flex-1 bg-white p-6 overflow-y-auto">
      {/* Click-outside “X” bar */}
      <div className="flex justify-end mb-4">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700"
        >✕</button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          {/* Company */}
          {editField === 'company_name' ? (
            <input
              autoFocus
              className="text-2xl font-bold mb-1 border-b"
              value={lead.company_name}
              onChange={e => setLead(l => ({ ...l, company_name: e.target.value }))}
              onBlur={() => save('company_name', lead.company_name)}
            />
          ) : (
            <h2
              className="text-2xl font-bold mb-1 cursor-pointer"
              onClick={() => setEditField('company_name')}
            >
              Company: {lead.company_name}
            </h2>
          )}

          {/* Owner */}
          {editField === 'owner_name' ? (
            <input
              className="text-sm mt-1 border-b"
              value={lead.owner_name}
              onChange={e => setLead(l => ({ ...l, owner_name: e.target.value }))}
              onBlur={() => save('owner_name', lead.owner_name)}
            />
          ) : (
            <p
              className="text-sm mt-1 cursor-pointer"
              onClick={() => setEditField('owner_name')}
            >
              Owner: {lead.owner_name}
            </p>
          )}
          {/* Website */}
          {editField === 'website' ? (
            <input
              className="text-sm mt-1 border-b"
              value={lead.website || ''}
              placeholder="Website URL"
              onChange={e => setLead(l => ({ ...l, website: e.target.value }))}
              onBlur={() => save('website', lead.website)}
            />
          ) : (
            <p
              className="text-sm mt-1 cursor-pointer"
              onClick={() => setEditField('website')}
            >
              Website:{' '}
              <a
                href={lead.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2563EB] hover:underline"
              >
                {lead.website || '—'}
              </a>
            </p>
          )}

          

          {/* Property Count */}
          {editField === 'property_count_estimate' ? (
            <input
              type="number"
              className="text-sm mt-1 border-b"
              value={lead.enrichment?.property_count_estimate || ''}
              onChange={e => {
                const val = e.target.value;
                setLead(l => ({
                  ...l,
                  enrichment: {
                    ...l.enrichment,
                    property_count_estimate: val
                  }
                }));
              }}
              onBlur={() =>
                save(
                  'enrichment.property_count_estimate',
                  lead.enrichment.property_count_estimate
                )
              }
            />
          ) : (
            <p
              className="text-sm mt-1 cursor-pointer"
              onClick={() => setEditField('property_count_estimate')}
            >
              Properties: {lead.enrichment?.property_count_estimate ?? '—'}
            </p>
          )}
        </div>
{/* 
        <button
          className="px-3 py-1 rounded bg-red-500 text-white"
          onClick={() => onDelete(lead._id)}
        >Delete</button> */}
      </div>

      {/* Status (unchanged by edits) */}
      <div className="mb-6 flex items-center space-x-4">
        <label className="text-sm font-semibold text-[#1F2937]">Status</label>

        {lead.status !== 'closed' ? (
          <button
            className="px-3 py-1 bg-[#EF4444] text-[#FFFFFF] rounded-[8px] text-sm"
            onClick={() => {
              // remember what we’re about to overwrite
              setPrevStatus(lead.status);
              dispatch(updateStatus({ leadId, status: 'closed' }))
                .unwrap()
                .then(() => fetchLead())
                .catch(console.error);
            }}
          >
            Close Lead
          </button>
        ) : (
          <>
            <span className="px-3 py-1 bg-[#F3F4F6] text-[#6B7280] rounded-[8px] text-sm">
              Closed
            </span>
            <button
              className="px-3 py-1 bg-[#2563EB] text-white rounded-[8px] text-sm"
              onClick={() => {
                dispatch(updateStatus({ leadId, status: prevStatus }))
                  .unwrap()
                  .then(() => setLead(l => ({ ...l, status: prevStatus })))
                  .catch(console.error);
              }}
            >
              Undo
            </button>
          </>
        )}
      </div>



      {/* Email Composer */}
      <EmailComposer
        leadId={lead._id}
        initialTo={selectedTo}
        threadId={currentThreadId}
        onClearTo={() => setSelectedTo('')}
        onSent={newStatus => {
          // bubble it up
          onEmailSent(newStatus);
        }}
      />

      {/* Emails */}
      <section className="mb-6">
        <h4 className="text-sm font-semibold mb-2">Found Emails</h4>
        <ul className="space-y-1 mb-2">
          {lead.emails.map(e => (
            <li key={e.address} className="flex justify-between items-center">
              <span
                className="text-sm cursor-pointer hover:underline"
                onClick={() => setSelectedTo(e.address)}
              >{e.address}</span>
            </li>
          ))}
        </ul>
        <details className="bg-gray-50 rounded overflow-hidden">
          <summary className="px-2 py-1 text-sm text-blue-600 hover:underline cursor-pointer">
            + Add Email
          </summary>
          <div className="p-2">
            <AddEmailForm leadId={lead._id} />
          </div>
        </details>
      </section>


{/* Contacts */}
<details className="bg-gray-50 rounded-2xl transition-all duration-300 overflow-hidden mb-6 border border-[#E5E7EB]">
  <summary className="flex items-center justify-between px-4 py-2 cursor-pointer">
    <span className="text-sm font-semibold text-[#1F2937]">
      Contacts ({lead.people.length})
    </span>
    <svg
      className="w-4 h-4 text-[#6B7280] transition-transform duration-200"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.646 6.646a.5.5 0 01.708 0L8 9.293l2.646-2.647a.5.5 0 11.708.707l-3 3a.5.5 0 01-.708 0l-3-3a.5.5 0 010-.707z"
        fill="currentColor"
      />
    </svg>
  </summary>

  <div className="mt-2 px-4 pb-4 space-y-4">
    {lead.people.map(p => (
      <div key={p.email || p.full_name} className="p-4 bg-white rounded-lg shadow-sm">
        <p className="font-medium text-[#1F2937]">
          {p.full_name ||
            (p.first_name && p.last_name
              ? `${p.first_name} ${p.last_name}`
              : p.first_name) ||
            ''}
        </p>
        {p.title && (
          <p className="text-xs text-[#6B7280] mt-1">{p.title}</p>
        )}

        {p.potential_emails?.length > 0 && (
          <ul className="mt-3 space-y-2 text-sm text-[#374151]">
            {p.potential_emails.map(email => (
              <li key={email} className="justify-between items-center">
                <span className="">{email}</span>
                <button
                  className="w-6 h-6 items-center rounded-full bg-[#E0F2FE] text-[#2563EB] hover:bg-[#BFDBFE] transition-colors duration-200"
                  onClick={() => setSelectedTo(email)}
                  title={`Email ${email}`}
                >
                  ✉️
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    ))}

    <details className="border-t border-[#E5E7EB] pt-4">
      <summary className="flex items-center space-x-2 text-sm text-[#2563EB] cursor-pointer select-none">
        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#E0F2FE] text-[#2563EB] hover:bg-[#BFDBFE] transition-colors duration-200">
          +
        </span>
        <span>Add Contact</span>
      </summary>
      <div className="mt-3">
        <AddPersonForm leadId={lead._id} onSuccess={fetchLead} />
      </div>
    </details>
  </div>
</details>




      {/* Notes */}
      <details open className="bg-white border border-[#E5E7EB] rounded-2xl p-6 mb-6 shadow-sm">
        <summary className="flex items-center justify-between text-sm font-semibold text-[#1F2937] cursor-pointer">
          Notes History
          <svg
            className="w-4 h-4 text-[#6B7280] transition-transform duration-200"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.646 6.646a.5.5 0 01.708 0L8 9.293l2.646-2.647a.5.5 0 11.708.707l-3 3a.5.5 0 01-.708 0l-3-3a.5.5 0 010-.707z"
              fill="currentColor"
            />
          </svg>
        </summary>

        {/* existing history entries */}
        <div className="mt-4 space-y-4">
          {lead.notes_history.map((h, i) => (
            <div key={i} className="flex flex-col bg-[#F3F4F6] rounded-lg p-4">
              <span className="text-[10px] text-[#9CA3AF] mb-1">
                {new Date(h.createdAt).toLocaleString()}
              </span>
              <p className="text-sm text-[#374151]">{h.content}</p>
            </div>
          ))}
        </div>

        {/* inline editor */}
        <div className="mt-6 border-t border-[#E5E7EB] pt-6">
  <NotesEditor
  leadId={lead._id}
  initial=""
  onSave={handleSaveNotes}
/>

        </div>
      </details>


      {/* Thread */}
      <section>
        <h4 className="text-sm font-semibold mb-2">Thread</h4>
        <EmailThread messages={lead.messages} onReply={handleReply} />
      </section>
    </div>
  );
}
